/**
 * Locale Context Provider
 * Provides locale settings throughout the app
 */

import React, { createContext, useContext, useMemo } from 'react'
import useUserPreferencesStore from '../../store/UserPreferencesStore'
import { SUPPORTED_LOCALES } from './config'
import type { DateFormatType, SupportedLocale, TimeFormatType } from './config'

export interface LocaleContextValue {
  locale: SupportedLocale
  dateFormat: DateFormatType
  timeFormat: TimeFormatType
  timezone: string
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6
  firstDayOfWeek: number
  is24Hour: boolean
  localeConfig: (typeof SUPPORTED_LOCALES)[SupportedLocale]
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined)

export interface LocaleProviderProps {
  children: React.ReactNode
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const language = useUserPreferencesStore((state) => state.language)
  const dateFormat = useUserPreferencesStore((state) => state.dateFormat)
  const timeFormat = useUserPreferencesStore((state) => state.timeFormat)
  const timezone = useUserPreferencesStore((state) => state.timezone)
  const weekStartsOn = useUserPreferencesStore((state) => state.weekStartsOn)

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale: language,
      dateFormat,
      timeFormat,
      timezone,
      weekStartsOn,
      firstDayOfWeek: weekStartsOn,
      is24Hour: timeFormat === '24h',
      localeConfig: SUPPORTED_LOCALES[language],
    }),
    [language, dateFormat, timeFormat, timezone, weekStartsOn],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

/**
 * Hook to access locale context
 */
export const useLocale = (): LocaleContextValue => {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return context
}

/**
 * Hook to get locale-aware Intl.DateTimeFormat options
 */
export const useDateTimeFormatOptions = () => {
  const { locale, is24Hour } = useLocale()

  return useMemo(() => {
    const hourCycle = is24Hour ? 'h23' : 'h12'
    return {
      locale,
      hourCycle,
      dateFormatOptions: {
        year: 'numeric' as const,
        month: 'short' as const,
        day: 'numeric' as const,
      },
      timeFormatOptions: {
        hour: 'numeric' as const,
        minute: '2-digit' as const,
        hourCycle,
      },
      dateTimeFormatOptions: {
        year: 'numeric' as const,
        month: 'short' as const,
        day: 'numeric' as const,
        hour: 'numeric' as const,
        minute: '2-digit' as const,
        hourCycle,
      },
    }
  }, [locale, is24Hour])
}
